import { useState, useEffect } from "react";
import { Download, X, Share, MoreVertical, Monitor } from "lucide-react";
import { usePwaInstall, type InstallPlatform } from "@/lib/use-pwa-install";

interface PwaInstallPromptProps {
  /** Automatically show after delay */
  autoShow?: boolean;
  /** Delay in ms before showing (default: 10000) */
  delay?: number;
}

interface PlatformInstructions {
  title: string;
  description: string;
  steps?: string[];
  icon: React.ReactNode;
  actionLabel?: string;
}

function getInstructions(
  platform: InstallPlatform,
  hasNativePrompt: boolean,
): PlatformInstructions {
  if (hasNativePrompt) {
    return {
      title: "Install Vizi Fan Mix",
      description: "Install the organizer app for quick access.",
      icon: <Download className="w-5 h-5" />,
      actionLabel: "Install",
    };
  }

  switch (platform) {
    case "safari-ios":
      return {
        title: "Add to Home Screen",
        description: "Install the organizer app for quick access.",
        steps: [
          "Tap the Share button in the toolbar",
          "Scroll down and tap \"Add to Home Screen\"",
          "Tap \"Add\" to confirm",
        ],
        icon: <Share className="w-5 h-5" />,
      };
    case "safari-desktop":
      return {
        title: "Add to Dock",
        description: "Install the organizer app for quick access.",
        steps: [
          "Click File in the menu bar",
          "Click \"Add to Dock\"",
        ],
        icon: <Monitor className="w-5 h-5" />,
      };
    case "android-chrome":
      return {
        title: "Add to Home Screen",
        description: "Install the organizer app for quick access.",
        steps: [
          "Tap the menu button (three dots)",
          "Tap \"Add to Home screen\"",
          "Tap \"Add\" to confirm",
        ],
        icon: <MoreVertical className="w-5 h-5" />,
      };
    default:
      return {
        title: "Install Vizi Fan Mix",
        description: "Install the organizer app for quick access.",
        icon: <Download className="w-5 h-5" />,
      };
  }
}

export function PwaInstallPrompt({
  autoShow = false,
  delay = 10000,
}: PwaInstallPromptProps) {
  const {
    platform,
    canInstall,
    isInstalled,
    hasNativePrompt,
    isDismissed,
    promptInstall,
    dismissPrompt,
  } = usePwaInstall();
  const [isVisible, setIsVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Handle auto-show with delay
  useEffect(() => {
    if (!autoShow || !canInstall || isInstalled || isDismissed) {
      return;
    }

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [autoShow, delay, canInstall, isInstalled, isDismissed]);

  const instructions = getInstructions(platform, hasNativePrompt);

  if (!isVisible || isInstalled) return null;

  function handleInstall() {
    if (hasNativePrompt) {
      promptInstall();
      setIsVisible(false);
    } else {
      setShowInstructions(true);
    }
  }

  function handleDismiss() {
    dismissPrompt();
    setIsVisible(false);
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <div className="bg-vizimix-800 border border-vizimix-700 rounded-xl p-4 shadow-2xl shadow-black/40">
        <div className="flex items-start gap-3">
          <div className="text-vizimix-400 mt-0.5 shrink-0">
            {instructions.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-vizimix-100">
              {instructions.title}
            </p>
            <p className="text-xs text-vizimix-400 mt-1">
              {instructions.description}
            </p>

            {showInstructions && instructions.steps && (
              <ol className="mt-3 space-y-1.5">
                {instructions.steps.map((step, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-xs text-vizimix-400"
                  >
                    <span className="flex items-center justify-center w-4 h-4 bg-vizimix-700 rounded-full text-[10px] font-medium shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="text-vizimix-400 hover:text-vizimix-100 transition-colors shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!showInstructions && (
          <div className="flex gap-2 mt-3 ml-8">
            <button
              onClick={handleInstall}
              className="px-3 py-1.5 bg-vizimix-600 hover:bg-vizimix-500 text-white text-xs font-medium rounded-lg transition-colors"
            >
              {instructions.actionLabel || "How to install"}
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 bg-vizimix-700/50 hover:bg-vizimix-700 text-vizimix-400 text-xs font-medium rounded-lg transition-colors"
            >
              Not now
            </button>
          </div>
        )}

        {showInstructions && (
          <div className="flex gap-2 mt-3 ml-8">
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 bg-vizimix-700/50 hover:bg-vizimix-700 text-vizimix-400 text-xs font-medium rounded-lg transition-colors"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
